/**
 * Users API Service
 * Handles all user-related API calls
 */

import apiClient from './index.js';

export class UsersApi {
  /**
   * Get user templates
   * @param {string} token - Auth token
   */
  static async getTemplates(token) {
    return apiClient.get('/users/v1/templates/list', token);
  }

  /**
   * Register a new user
   * @param {string} token - Auth token
   * @param {Object} userData - User registration data
   */
  static async register(token, userData) {
    return apiClient.post('/users/v1/register', token, userData);
  }

  /**
   * Update user profile
   * @param {string} token - Auth token
   * @param {Object} profileData - Profile data
   */
  static async updateProfile(token, profileData) {
    return apiClient.post('/users/v1/profile', token, profileData);
  }

  /**
   * Subscribe to a tier
   * @param {string} token - Auth token
   * @param {Object} subscriptionData - Subscription data
   * @param {string} subscriptionData.type - 'create_session' | 'update_subscription' | 'cancel_subscription'
   * @param {number} [subscriptionData.tier] - Tier number (1-4) for modern approach
   * @param {string} [subscriptionData.interval] - 'monthly' | 'annual' for modern approach
   * @param {string} [subscriptionData.plan] - Plan name ('starter', 'basic', 'plus', 'premium')
   * @param {string[]} [subscriptionData.addons] - Array of addon types like ['storage']
   * @param {string} [subscriptionData.priceId] - Legacy direct price ID approach
   * @param {string} [subscriptionData.addonPriceId] - Legacy addon price ID approach
   * @param {string} [subscriptionData.subscriptionId] - Required for updates/cancellations
   */
  static async subscribe(token, subscriptionData) {
    return apiClient.post('/users/v1/subscribe', token, subscriptionData);
  }

  /**
   * Update existing subscription
   * @param {string} token - Auth token
   * @param {string} subscriptionId - Stripe subscription ID
   * @param {number} tier - New tier number (1-4)
   * @param {string} interval - New interval ('monthly' | 'annual')
   * @param {string} [plan] - Plan name for precise matching
   */
  static async updateSubscription(token, subscriptionId, tier, interval, plan) {
    return this.subscribe(token, {
      type: 'update_subscription',
      subscriptionId,
      tier,
      interval,
      plan
    });
  }

  /**
   * Cancel existing subscription
   * @param {string} token - Auth token
   * @param {string} subscriptionId - Stripe subscription ID
   */
  static async cancelSubscription(token, subscriptionId) {
    return this.subscribe(token, {
      type: 'cancel_subscription',
      subscriptionId
    });
  }

  /**
   * Get monthly distribution setting
   * @param {string} token - Auth token
   */
  static async getMonthlyDistributionSetting(token) {
    return apiClient.post('/users/v1/profile', token, { type: 'details' });
  }

  /**
   * Update monthly distribution setting
   * @param {string} token - Auth token
   * @param {boolean} enabled - Whether monthly distribution is enabled
   */
  static async updateMonthlyDistributionSetting(token, enabled) {
    return apiClient.post('/users/v1/profile', token, { type: 'update', monthlyDistributionEnabled: enabled });
  }

  /**
   * Get theme mode preference.
   * @param {string} token - Auth token
   */
  static async getThemeMode(token) {
    return apiClient.post('/users/v1/profile', token, { type: 'details' });
  }

  /**
   * Update theme mode preference.
   * @param {string} token - Auth token
   * @param {'light' | 'dark'} themeMode - Desired theme mode
   */
  static async updateThemeMode(token, themeMode) {
    return apiClient.post('/users/v1/profile', token, { type: 'update', themeMode });
  }

  /**
   * Submit feedback
   * @param {string} token - Auth token
   * @param {Object} feedbackData - Feedback data
   */
  static async submitFeedback(token, feedbackData) {
    return apiClient.post('/users/v1/feedback', token, feedbackData);
  }
}

export default UsersApi;
